import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { mergeClassNames } from "../../utils/classes";
import { reposition } from "./tooltipUtils";

import type { TooltipPlacement } from "./tooltipUtils";
import type { ReactNode } from "react";

import * as styles from "./tooltip.scss";

export interface TooltipProps {
	offset?: number;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	onVisibilityChange?: (open: boolean) => void;
	contentGenerator: ReactNode | (() => ReactNode);
	children: ReactNode[] | ReactNode;
	color?: "black" | "white";
	placement?: TooltipPlacement;
	showArrow?: boolean;
	noContentPadding?: boolean;
	wrapperClassName?: string;
	tooltipClassName?: string;
	arrowOffsetClassName?: string;
	triggerTestId?: string;
	popoverTestId?: string;
	stopPropagation?: boolean;
	portalTargetId?: string;
}

export const Tooltip = ({
	open: controlledOpen,
	onOpenChange,
	onVisibilityChange,
	color,
	noContentPadding,
	contentGenerator,
	showArrow = true,
	children,
	placement = "bottom",
	wrapperClassName,
	tooltipClassName,
	arrowOffsetClassName,
	triggerTestId: testId,
	popoverTestId,
	offset = 8,
	stopPropagation = false,
	portalTargetId = "tooltip-root",
}: TooltipProps) => {
	const isControlled = controlledOpen !== undefined;
	const [uncontrolledOpen, setUncontrolledOpen] = useState(false);

	// Turn this into a prop if you find the need for it
	const padding = 10;

	const open = controlledOpen ?? uncontrolledOpen;

	const requestOpenChange = useCallback(
		(nextOpen: boolean) => {
			if (!isControlled) {
				setUncontrolledOpen(nextOpen);
			}

			if (nextOpen !== open) {
				onOpenChange?.(nextOpen);
			}
		},
		[isControlled, onOpenChange, open],
	);

	// Weird hack to useEffect
	const openStateTracking = useRef(false);

	const popper = useRef(null);
	const reference = useRef<HTMLDivElement>(null);
	const arrow = useRef<HTMLDivElement>(null);
	const content = useRef<HTMLDivElement>(null);

	const positionTooltip = useCallback(() => {
		if (popper.current && reference.current) {
			const repositionOptions = {
				offset,
				padding,
				placement,
				...(arrow.current ? { arrow: arrow.current } : {}),
			};

			// Set tooltip margin to resolve w/h in the viewport
			(popper.current as HTMLElement).style.setProperty("--tooltipMargin", `${padding}px`);
			reposition(reference.current, popper.current, repositionOptions);
			// Increase pseudo-element boundaries to facilitate interacting with the tooltip
			(popper.current as HTMLElement).style.setProperty("--tooltipOffset", `-${offset + 2}px`);
			// If the user scrolls out-of-bounds, close the tooltip
		}
	}, [offset, placement]);

	const openTooltip = useCallback(() => {
		positionTooltip();
		requestOpenChange(true);
	}, [positionTooltip, requestOpenChange]);

	/** This is to make the tooltipContent scrollable. Without it, scrolling inside the tooltip would
	reposition/rerender the entire tooltip because of the onCapture flag on `addEventListener`.*/
	const trackScroll = useCallback(
		(ev: Event) => {
			if ((ev.target as HTMLElement) !== content.current) {
				positionTooltip();
			}
		},
		[positionTooltip],
	);

	// Capture all scroll events
	useEffect(() => {
		if (open && !openStateTracking.current) {
			positionTooltip();
			document.addEventListener("scroll", trackScroll, true);
			onVisibilityChange?.(true);
		} else if (!open && openStateTracking.current) {
			document.removeEventListener("scroll", trackScroll, true);
			onVisibilityChange?.(false);
		}
		openStateTracking.current = open;

		return () => {
			document.removeEventListener("scroll", trackScroll, true);
		};
	}, [onVisibilityChange, open, positionTooltip, trackScroll]);

	const portalTarget = document.getElementById(portalTargetId) ?? document.body;

	return (
		<>
			<div
				ref={reference}
				className={mergeClassNames(styles.tooltipTrigger, wrapperClassName)}
				onMouseOver={openTooltip}
				onMouseLeave={() => requestOpenChange(false)}
				data-testid={testId}
				onClick={stopPropagation ? e => e.stopPropagation() : undefined}
			>
				{children}
			</div>
			{createPortal(
				<div
					className={mergeClassNames(styles.tooltip, color === "black" ? styles.black : styles.white, tooltipClassName)}
					ref={popper}
					data-open={open}
					onMouseEnter={open ? openTooltip : undefined}
					onMouseLeave={open ? () => requestOpenChange(false) : undefined}
					data-testid={popoverTestId}
				>
					{showArrow && <div ref={arrow} className={mergeClassNames(styles.arrow, arrowOffsetClassName)}></div>}
					<div
						ref={content}
						className={mergeClassNames(styles.tooltipContent, noContentPadding ? styles.paddingOff : styles.paddingOn)}
					>
						{typeof contentGenerator === "function" ? contentGenerator() : contentGenerator}
					</div>
				</div>,
				portalTarget,
			)}
		</>
	);
};

export default Tooltip;
